/**
 * Validation helper utilities for chat components
 * Pure functions for file validation and error handling
 */

/**
 * Represents a validation error for a specific file
 */
export interface ValidationError {
  /** The file that failed validation */
  file: File;
  /** Human-readable error message */
  message: string;
  /** Optional categorization of the error type */
  errorType?: string;
}

/**
 * Result of file validation containing validity status and any errors
 */
export interface ValidationResult {
  /** Whether all files passed validation */
  isValid: boolean;
  /** List of validation errors if any */
  errors: ValidationError[];
}

/**
 * Creates a consolidated Error object from multiple validation errors
 * Combines all error messages into a single error for throwing
 *
 * @param errors - Array of validation errors
 * @returns Error object with combined error messages
 */
export const createValidationError = (errors: ValidationError[]): Error => {
  const errorMessages = errors.map((error) => error.message).join("\n");

  return new Error(errorMessages);
};

/**
 * Prepares a user-friendly error message from validation errors
 * Formats single errors as plain text and multiple errors as numbered list
 *
 * @param errors - Array of validation errors
 * @returns Formatted error message string
 */
export const prepareErrorMessage = (errors: ValidationError[]): string => {
  if (errors.length === 0) {
    return "";
  }

  if (errors.length === 1) {
    return errors[0].message;
  }

  // Multiple errors - format as bullet list
  return errors.map((error, index) => `${index + 1}. ${error.message}`).join("\n");
};

/**
 * Determines if file upload should be blocked based on validation result
 * Used to prevent invalid files from being uploaded
 *
 * @param validationResult - Result of file validation
 * @returns True if upload should be blocked, false otherwise
 */
export const shouldBlockUpload = (validationResult: ValidationResult): boolean => {
  return !validationResult.isValid;
};

/**
 * Gets the first failed file from validation errors
 * Used to provide context when showing error messages
 *
 * @param errors - Array of validation errors
 * @returns First file that failed validation, or undefined if no errors
 */
export const getFirstFailedFile = (errors: ValidationError[]): File | undefined => {
  return errors[0]?.file;
};

/**
 * Creates a structured error object with additional metadata
 * Allows attaching extra context for enhanced error handling
 *
 * @param message - Primary error message
 * @param structuredData - Optional structured data to attach
 * @returns Error object with structured data attached
 */
export const createStructuredUploadError = (message: string, structuredData?: any): Error => {
  const error = new Error(message);

  if (structuredData) {
    (error as any).structuredError = structuredData;
  }

  return error;
};

/**
 * Checks if an error has structured data attached
 * Used to determine if enhanced error handling is available
 *
 * @param error - Error object to check
 * @returns True if error has structured data, false otherwise
 */
export const hasStructuredError = (error: Error): boolean => {
  return !!(error as any).structuredError;
};

/**
 * Extracts structured error data if present
 * Retrieves additional context attached to structured errors
 *
 * @param error - Error object to extract data from
 * @returns Structured data if present, undefined otherwise
 */
export const getStructuredErrorData = (error: Error): any | undefined => {
  return (error as any).structuredError;
};

/**
 * Creates a formatted error summary for UI display
 * Handles both regular and structured errors with appropriate formatting
 *
 * @param error - Error object to summarize
 * @param file - Optional file context for the error
 * @returns Object with title, message, and optional details
 */
export const createErrorSummary = (
  error: Error,
  file?: File,
): { title: string; message: string; details?: string } => {
  const structuredData = getStructuredErrorData(error);

  if (structuredData?.error) {
    return {
      title: structuredData.error.title || "Upload Failed",
      message: error.message,
      details: structuredData.error.technical_details,
    };
  }

  return {
    title: file ? `Failed to upload ${file.name}` : "Upload Failed",
    message: error.message,
  };
};

/**
 * Determines the severity level of a validation error
 * Used for appropriate UI styling and user feedback
 *
 * @param error - Validation error to assess
 * @returns Severity level: "warning", "error", or "info"
 */
export const getErrorSeverity = (error: ValidationError): "warning" | "error" | "info" => {
  switch (error.errorType) {
    case "size_limit":
      return "warning";
    case "blocked_extension":
    case "security":
      return "error";
    default:
      return "info";
  }
};

/**
 * Groups validation errors by their type for organized display
 * Useful for showing categorized error summaries in the UI
 *
 * @param errors - Array of validation errors to group
 * @returns Object with error types as keys and arrays of errors as values
 */
export const groupErrorsByType = (errors: ValidationError[]): Record<string, ValidationError[]> => {
  return errors.reduce(
    (groups, error) => {
      const type = error.errorType || "general";

      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(error);

      return groups;
    },
    {} as Record<string, ValidationError[]>,
  );
};
